import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { StateProvider, ColumnStore, GridColumnConfig, Model, StringHelper, CellTooltip, MenuItemConfig, Menu } from '@bryntum/grid';
import { BryntumGridModule, BryntumGridComponent, BryntumGridProps } from '@bryntum/grid-angular';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { bootstrapArrowsAngleContract, bootstrapArrowsAngleExpand } from '@ng-icons/bootstrap-icons';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { Store } from '@ngrx/store';
import dayjs from 'dayjs';
import { take } from 'rxjs';
import writeXlsxFile, { Cell } from 'write-excel-file/browser';
import { ContainerService } from '../../../../../shared/Container/container.service';
import { selectUserType } from '../../../../../shared/app-configuration/app-configuration.reducer';
import { UserType } from '../../../../../shared/app-configuration/models/UserType';
import { BlobStorageService } from '../../../../../shared/blob-storage/blob-storage.service';
import { BryntumGridStateProvider } from '../../../../../shared/bryntum/bryntum-grid-state-provider';
import { BryntumGridStateService } from '../../../../../shared/bryntum/bryntum-grid-state.service';
import { ProofOfDeliveryPdfService } from '../../../../../shared/proof-of-delivery/proof-of-delivery-pdf.service';
import { IRootState } from '../../../../app.config';
import { IContainerSearchState, selectContainerSearch } from '../../../../../shared/Container/container.reducer';
import { ContainerStatusColumnConfig } from '../../../../../shared/Container/constant/ContainerStatusColumnConfig';
import { HttpErrorResponse } from '@angular/common/http';
import { ToasterService } from '../../../../../shared/toaster/toaster.service';
import { ContainerJobCompleteModalComponent } from '../container-job-complete-modal/container-job-complete-modal.component';
import { MovementStatusEnum } from '../../../../../shared/Jobbing/enums/MovementStatusEnum';
import { ContainerDisplayStatus } from '../../../../../shared/Container/model/ContainerDisplayStatus';
import { getBooleanColumnGenericConfig, getNumberColumnGenericConfig, getTimeColumnGenericConfig } from '../../../../../shared/bryntum/Helper/MogoBryntumGridConfigHelper';
import { ContainerJobEyeViewModalComponent } from '../../../../../shared/modals/container-job-eye-view-modal/container-job-eye-view-modal.component';
import ContainerJobGridStatusMovement from '../../../../../shared/Container/model/ContainerJobGridStatusMovement';
import { DateTimeHelper } from '../../../../../shared/DateTime/DateTimeHelper';

@Component({
  selector: 'app-container-job-leg-status',
  standalone: true,
  imports: [BryntumGridModule, NgIcon],
  templateUrl: './container-job-leg-status.component.html',
  styleUrl: './container-job-leg-status.component.scss',
  viewProviders: [
    provideIcons({
      bootstrapArrowsAngleContract,
      bootstrapArrowsAngleExpand,
    })
  ]
})

export class ContainerJobLegStatusComponent implements OnInit, OnDestroy {
  data: ContainerJobGridStatusMovement[] = [];
  totalItems = 0;
  isInitializing = true;
  isLoadingJobs = false;
  gridSelected: ContainerJobGridStatusMovement[] = [];
  selectedTotalItems = 0;
  containerGridStateId = 'containerLegGrid';
  userType: UserType | null = null;
  containerLocationTypeThatDisplayEmptyPark: string[] = ['Container park', 'MT park'];
  gridHeight: string = '79vh';
  private _containerGrid: BryntumGridComponent | null = null;
  @ViewChild('containerGrid')
  set containerGrid(value: BryntumGridComponent | null) {
    this._containerGrid = value;
    this.syncGridMask();
    this.updateTotalItemsFromStore();
  }
  get containerGrid(): BryntumGridComponent | null {
    return this._containerGrid;
  }

  private gridStateProvider: BryntumGridStateProvider = new BryntumGridStateProvider(this.bryntumGridStateService, 34, this.containerGridStateId);

  constructor(private router: Router,
    private store$: Store<IRootState>,
    private modalService: NgbModal,
    private proofOfDeliveryPdfService: ProofOfDeliveryPdfService,
    private blobStorageService: BlobStorageService,
    private containerService: ContainerService,
    private bryntumGridStateService: BryntumGridStateService,
    private toasterService: ToasterService
  ) { }

  gridProps: BryntumGridProps | null = null;
  searchDateRange: IContainerSearchState['searchParams'] | null = null;
  private pendingSearchDateRange: IContainerSearchState['searchParams'] | null = null;

  isFreightUser = (): boolean => this.userType === UserType.FreightPayer;
  isSubUser = (): boolean => this.userType === UserType.Subcontractor;

  ngOnDestroy(): void {
    if (this.gridStateProvider) {
      this.gridStateProvider.destroy();
    }
  }

  ngOnInit() {
    this.isInitializing = true;
    this.data = [];

    this.gridStateProvider
    .init()
    .then(() => this.initialiseGrid());

    this.store$.select(selectContainerSearch).subscribe(result => {
      const fromDayJs = dayjs(result.from).add(1, 'day');
      const toDayJs = dayjs(result.to).add(1, 'day');
      this.searchDateRange = {
        from: new Date(fromDayJs.toString()),
        to: new Date(toDayJs.toString()),
        searchDateOption: result.searchDateOption
      };

      this.loadJobsList(this.searchDateRange);
    });
  }

  private initialiseGrid() {
    this.store$.select(selectUserType).subscribe((userType) => {
      this.userType = userType;

      let columns: (GridColumnConfig & { field: string, excelFormat?: string | ((value: number) => string) })[] = [
        {
          id: 'job_date', field: 'jobDateLocal', text: 'Job Date', autoWidth: 1, type: 'date', format: 'DD/MM/YYYY HH:mm', excelFormat: 'dd/mm/yyyy hh:mm',
          renderer: ({ record, isExport }: { record: Partial<Model & ContainerJobGridStatusMovement>, isExport: boolean }) => {
            return record.jobDateLocal && record.jobDateLocal.toString() !== 'Invalid Date' ? dayjs(record.jobDateLocal).format('DD/MM/YYYY HH:mm') : '';
          }
        },
        {
          id: 'requested_delivery_date', field: 'requestedDeliveryDate', text: 'Requested Delivery Date', autoWidth: 1, type: 'date', format: 'DD/MM/YYYY', excelFormat: 'dd/mm/yyyy', editor: false,
        },
        {
          id: 'full_leg_completed_date', field: 'fullLegCompletedDate', text: 'Full MVT Completed Date', autoWidth: 1, type: 'date', format: 'DD/MM/YYYY HH:mm', excelFormat: 'dd/mm/yyyy hh:mm',
          renderer: ({ record, isExport }: { record: Partial<Model & ContainerJobGridStatusMovement>, isExport: boolean }) => {
            return record.fullLegCompletedDate && record.fullLegCompletedDate.toString() !== 'Invalid Date' ? dayjs(record.fullLegCompletedDate).format('DD/MM/YYYY HH:mm') : '';
          }
        },
        {
          id: 'dehire_leg_completed_date', field: 'dehireLegCompletedDate', text: 'Dehire MVT Completed Date', autoWidth: 1, type: 'date', format: 'DD/MM/YYYY HH:mm', excelFormat: 'dd/mm/yyyy hh:mm',
          renderer: ({ record, isExport }: { record: Partial<Model & ContainerJobGridStatusMovement>, isExport: boolean }) => {
            return record.dehireLegCompletedDate && record.dehireLegCompletedDate.toString() !== 'Invalid Date' ? dayjs(record.dehireLegCompletedDate).format('DD/MM/YYYY HH:mm') : '';
          }
        },
        {
          id: 'movement_status',
          field: 'status.name',
          text: 'Movement Status',
          autoWidth: 1,
          htmlEncode: false,
          renderer: ({ record, isExport }: { record: Partial<Model & ContainerJobGridStatusMovement>, isExport: boolean }) => {

            if (isExport && record.status) {
              return record.status.name;
            }

            if (record && record.status) {
              return StringHelper.xss`<span
              class="vertical-center"
              style="background-color: ${record.status.colour};" >
              <strong>${record.status.name}</strong>
               </span>`;
            } else {
              return "";
            }
          },
        },
        {
          id: 'container_status_container_leg',
          field: "containerStatusCode",
          text: 'Container Status',
          align: "center",
          autoWidth: 1,
          htmlEncode: false,
          tooltipRenderer: ({ record, cellTooltip }: { record: Partial<ContainerJobGridStatusMovement>, cellTooltip: CellTooltip }) => {
            return new Promise(resolve => {
              this.containerService.getContainerPortStatus(record.containerJobItemId!)
                .subscribe({
                  next: (result) => {
                    const containerDisplayStatus: ContainerDisplayStatus = {
                      portStatus: { ...result },
                      transitStatus: null
                    }
                    resolve(ContainerStatusColumnConfig.toolTip(containerDisplayStatus, record.jobDirection!));
                  },
                  error: (errorResponse: HttpErrorResponse) => {
                    resolve(errorResponse.error);
                  }
                });
            })
          },
          renderer: ({ record, isExport }: { record: Partial<Model & ContainerJobGridStatusMovement>, isExport: boolean }) => {
            if (isExport) {
              return record.containerStatus?.transitStatus ?
                record.containerStatus?.transitStatus.code :
                record.containerStatus?.portStatus ?
                  record.containerStatus?.portStatus.status :
                  ''
            }

            if (record && record.containerStatus) {
              return ContainerStatusColumnConfig.content(record.containerStatus);
            } else {
              return "";
            }
          },
        },
        { id: 'order_number_container_leg', field: 'orderNumber', text: 'Order Number', autoWidth: 1 },
        { id: 'customer_reference', field: 'customerReference', text: 'Customer Reference', autoWidth: 1 },
        { id: 'container_number', field: 'containerNumber', text: 'Container Number', autoWidth: 1 },
        { id: 'job_type', field: 'jobType', text: 'Job Type', autoWidth: 1, ...this.getFilterableCombo('jobType') },
        {
          id: 'job_status', field: 'jobItemStatus', text: 'Job Status', autoWidth: 1, ...this.getFilterableCombo('jobItemStatus'),
          htmlEncode: false,
          renderer: ({ record, isExport }: { record: any, isExport: boolean }) => {
            if (isExport) {
              return record.jobItemStatus;
            } else {
              switch (record.jobItemStatus) {
                case "Active":
                  return `<span class="status vertical-center" style="background-color:#ea2088; color: white;">${record.jobItemStatus}</span>`;
                case "Completed":
                  return `<span class="status vertical-center" style="background-color:#62cb31; color: white;">${record.jobItemStatus}</span>`;
                case "Cancelled":
                  return `<span class="status vertical-center" style="background-color:#e43321; color: white;">${record.jobItemStatus}</span>`;
                case "Billed":
                  return `<span class="status vertical-center" style="background-color:#ffb606; color: white;">${record.jobItemStatus}</span>`;
                case "Subcontractor":
                  return `<span class="status vertical-center" style="background-color:#ffb606; color: white;">${record.jobItemStatus}</span>`;
                default:
                  return `<span class="status vertical-center">${record.jobItemStatus}</span>`;
              }
            }
          }
        },
        { id: 'container_size', field: 'containerSize', text: 'Size', autoWidth: 1 },
        { id: 'container_type', field: 'containerType', text: 'Type', autoWidth: 1 },
        { id: 'weight', field: 'weight', text: 'Weight', autoWidth: 1, type: 'number', ...getNumberColumnGenericConfig() },
        { id: 'door_direction', field: 'doorDirection', text: 'Door Direction', autoWidth: 1, ...this.getFilterableCombo('doorDirection') },
        {
          id: 'dangerous_goods',
          field: 'dangerousGoods',
          text: 'Dangerous Goods',
          autoWidth: 1,
          type: 'template',
          align: 'center',
          ...getBooleanColumnGenericConfig()
        },
        { id: 'dg_code', field: 'dgCode', text: 'DG Code', autoWidth: 1 },
        { id: 'ecn', field: 'ecn', text: 'ECN', autoWidth: 1 },
        { id: 'pin', field: 'pin', text: 'PIN', autoWidth: 1 },
        { id: 'express_pin', field: 'expressPin', text: 'Express PIN', autoWidth: 1 },
        { id: 'vessel_name', field: 'vesselName', text: 'Vessel Name', autoWidth: 1 },
        {
          id: 'vessel_eta_and_etd_date', field: 'vesselEtaAndEtdDate', text: 'Vessel ETA/D', autoWidth: 1, type: 'date', format: 'DD/MM/YYYY', excelFormat: 'dd/mm/yyyy',
          renderer: ({ record, isExport }: { record: Partial<Model & ContainerJobGridStatusMovement>, isExport: boolean }) => {
            return record.vesselEtaAndEtdDate && record.vesselEtaAndEtdDate.toString() !== 'Invalid Date' ? dayjs(record.vesselEtaAndEtdDate).format('DD/MM/YYYY') : '';
          }
        },
        { id: 'vessel_eta_and_etd_time', field: 'vesselEtaAndEtdTime', text: 'Vessel ETA/D Time', autoWidth: 1, ...getTimeColumnGenericConfig() },
        { id: 'original_port', field: 'originalPort', text: 'Original Port', autoWidth: 1, ...this.getFilterableCombo('originalPort') },
        { id: 'to_name', field: 'toName', text: 'Delivery Point Name', autoWidth: 1 },
        { id: 'from_name', field: 'fromName', text: 'Pickup Point Name', autoWidth: 1 },
        { id: 'demurrage_date', field: 'demurrageDate', text: 'Demurrage Date', autoWidth: 1, type: 'date', format: 'DD/MM/YYYY', excelFormat: 'dd/mm/yyyy' },
        { id: 'demurrage_time', field: 'demurrageTime', text: 'Demurrage Time', autoWidth: 1, type: 'time', format: 'HH:mm', ...getTimeColumnGenericConfig() },
        { id: 'demurrage_status', field: 'demurrageStatus', text: 'Demurrage Status', autoWidth: 1 },
        { id: 'detention_date', field: 'detentionDate', text: 'Detention Date', autoWidth: 1, type: 'date', format: 'DD/MM/YYYY', excelFormat: 'dd/mm/yyyy' },
        { id: 'detention_time', field: 'detentionTime', text: 'Detention Time', autoWidth: 1, type: 'time', format: 'HH:mm', ...getTimeColumnGenericConfig() },
        { id: 'receival_commence_date', field: 'receivalCommenceDate', text: 'Receival Commencement Date', autoWidth: 1, type: 'date', format: 'DD/MM/YYYY', excelFormat: 'dd/mm/yyyy' },
        { id: 'receival_commence_time', field: 'receivalCommenceTime', text: 'Receival Commencement Time', autoWidth: 1, type: 'time', format: 'HH:mm', ...getTimeColumnGenericConfig() },
        { id: 'receival_cutoff_date', field: 'receivalCutoffDate', text: 'Receival Cut-off Date', autoWidth: 1, type: 'date', format: 'DD/MM/YYYY', excelFormat: 'dd/mm/yyyy' },
        { id: 'receival_cutoff_time', field: 'receivalCutoffTime', text: 'Receival Cut-off Time', autoWidth: 1, type: 'time', format: 'HH:mm', ...getTimeColumnGenericConfig() },
        { id: 'gridNumber', field: 'gridNumber', text: 'Grid #', autoWidth: 1 },
        {
          id: 'full_leg_only',
          field: 'fullLegOnly',
          text: 'Full Leg Only',
          autoWidth: 1,
          type: 'template',
          align: 'center',
          ...getBooleanColumnGenericConfig(),
        },
        { id: 'live_unload', field: 'liveUnload', text: 'Live Unload', autoWidth: 1 },
        {
          id: 'mpi_inspection_required',
          field: 'mpiInspectionRequired',
          text: 'MPI Inspection Required',
          autoWidth: 1,
          type: 'template',
          align: 'center',
          ...getBooleanColumnGenericConfig(),
        },
        { id: 'to_address', field: 'toAddress', text: 'Delivery Point Address ', autoWidth: 1 },
        { id: 'from_address', field: 'fromAddress', text: 'Pickup Point Address', autoWidth: 1 },
        {
          id: 'devan_date', field: 'devanDate', text: 'Planned Devan Date ', autoWidth: 1, type: 'date', format: 'DD/MM/YYYY', excelFormat: 'dd/mm/yyyy',
          renderer: ({ record, isExport }: { record: Partial<Model & ContainerJobGridStatusMovement>, isExport: boolean }) => {
            return record.devanDate && record.devanDate.toString() !== 'Invalid Date' ? dayjs(record.devanDate).format('DD/MM/YYYY') : '';
          }
        },
        { id: 'mt_transfer_point', field: 'mtTransferPoint', text: 'MT Transfer Point', autoWidth: 1 },
        { id: 'acceptance_number', field: 'acceptanceNumber', text: 'Acceptance Number', autoWidth: 1 },
        { id: 'release_code', field: 'releaseCode', text: 'Release Code', autoWidth: 1 },
        { id: 'train_wagon_number', field: 'trainWagonNumber', text: 'Train Wagon Number', autoWidth: 1 },
        {
          id: 'acceptance_is_active',
          field: 'acceptanceIsActive',
          text: 'Acceptance Active',
          autoWidth: 1,
          type: 'template',
          align: 'center',
          ...getBooleanColumnGenericConfig(),
        },
        {
          id: 'release_is_active',
          field: 'releaseIsActive',
          text: 'Release Active',
          autoWidth: 1,
          type: 'template',
          align: 'center',
          ...getBooleanColumnGenericConfig(),
        },
        {
          id: 'first_notified_date', field: 'firstNotifiedDate', text: 'First Notified Date', autoWidth: 1, type: 'date', format: 'DD/MM/YYYY', excelFormat: 'dd/mm/yyyy',
          renderer: ({ record, isExport }: { record: Partial<Model & ContainerJobGridStatusMovement>, isExport: boolean }) => {
            return record.firstNotifiedDate && record.firstNotifiedDate.toString() !== 'Invalid Date' ? dayjs(record.firstNotifiedDate).format('DD/MM/YYYY') : '';
          }
        },
        {
          id: 'docs_received_date', field: 'docsReceivedDate', text: 'Docs received Date', autoWidth: 1, type: 'date', format: 'DD/MM/YYYY', excelFormat: 'dd/mm/yyyy',
          renderer: ({ record, isExport }: { record: Partial<Model & ContainerJobGridStatusMovement>, isExport: boolean }) => {
            return record.docsReceivedDate && record.docsReceivedDate.toString() !== 'Invalid Date' ? dayjs(record.docsReceivedDate).format('DD/MM/YYYY') : '';
          }
        },
        {
          id: 'docs_received_late', field: 'docsReceivedLate', text: 'Docs Received Late', autoWidth: 1,
          type: 'template',
          align: 'center',
          ...getBooleanColumnGenericConfig(),
        },
        { id: 'devan_type', field: 'devanType', text: 'Planned Devan Type', autoWidth: 1 },
        { id: 'outbound_vessel_name', field: 'outboundVesselName', text: 'Outbound Vessel Name', autoWidth: 1 },
        {
          id: 'needs_lift',
          field: 'needsLift',
          text: 'Needs Lift',
          autoWidth: 1,
          type: 'template',
          align: 'center',
          ...getBooleanColumnGenericConfig(),
        },
        {
          id: 'is_high_gauge',
          field: 'isHighGauge',
          text: 'High Gauge',
          autoWidth: 1,
          type: 'template',
          align: 'center',
          ...getBooleanColumnGenericConfig(),
        },
        {
          id: 'is_wide_gauge',
          field: 'isWideGauge',
          text: 'Wide Gauge',
          autoWidth: 1,
          type: 'template',
          align: 'center',
          ...getBooleanColumnGenericConfig(),
        },
        { id: 'sort_order', field: 'sortOrder', text: 'Leg No', autoWidth: 1, type: 'number', ...getNumberColumnGenericConfig() },
        { id: 'leg_type_name', field: 'legTypeName', text: 'Leg Type', autoWidth: 1 },
        {
          id: 'execution_date', field: 'executionDate', text: 'Execution Date', autoWidth: 1, type: 'date', format: 'DD/MM/YYYY', excelFormat: 'dd/mm/yyyy',
          renderer: ({ record, isExport }: { record: Partial<Model & ContainerJobGridStatusMovement>, isExport: boolean }) => {
            return record.executionDate && record.executionDate.toString() !== 'Invalid Date' ? dayjs(record.executionDate).format('DD/MM/YYYY') : '';
          }
        },
        { id: 'dwell_time_in_days', field: 'currentDwellTimeDays', text: 'Dwell Time (Days)', autoWidth: 1, tooltip: "Displays the current dwell duration for active containers or final dwell time for completed containers." },
        { id: 'container_shipping_line', field: 'containerOperatorName', text: 'Shipping Line', autoWidth: 1 },
        {
          id: 'container_discharge_date', field: 'dischargedDate', text: 'Discharged Date', autoWidth: 1, type: 'date', format: 'DD/MM/YYYY', excelFormat: 'dd/mm/yyyy',
          renderer: ({ record, isExport }: { record: Partial<Model & ContainerJobGridStatusMovement>, isExport: boolean }) => {
            return record.dischargedDate && record.dischargedDate.toString() !== 'Invalid Date' ? dayjs(record.dischargedDate).format('DD/MM/YYYY') : '';
          }
        },
        { id: 'container_discharge_time', field: 'dischargedTime', text: 'Discharged Time', autoWidth: 1, type: 'time', format: 'HH:mm', ...getTimeColumnGenericConfig() },
        { id: 'temperature', field: 'temperature', text: 'Temperature', autoWidth: 1, ...getNumberColumnGenericConfig() },
        {
          id: 'ready_for_pickup_date', field: 'readyForPickupDate', text: 'RFP Date Time', autoWidth: 1, type: 'date', format: 'DD/MM/YYYY HH:mm a', excelFormat: 'dd/mm/yyyy hh:mm',
          renderer: ({ record, isExport }: { record: Partial<Model & ContainerJobGridStatusMovement>, isExport: boolean }) => {
            return record.readyForPickupDate && record.readyForPickupDate.toString() !== 'Invalid Date' ? dayjs(record.readyForPickupDate).format('DD/MM/YYYY HH:mm a') : '';
          }
        },
        { id: 'container_location_name', field: 'containerLocationName', text: 'Location', autoWidth: 1 },
        {
          id: 'container_available_date', field: 'containerAvailableDateTime', text: 'Available Date', autoWidth: 1, type: 'date', format: 'DD/MM/YYYY HH:mm a', excelFormat: 'dd/mm/yyyy hh:mm',
          renderer: ({ record, isExport }: { record: Partial<Model & ContainerJobGridStatusMovement>, isExport: boolean }) => {
            return record.containerAvailableDateTime && record.containerAvailableDateTime.toString() !== 'Invalid Date' ? dayjs(record.containerAvailableDateTime).format('DD/MM/YYYY HH:mm a') : '';
          }
        },
        { id: 'seal_number', field: 'sealNumber', text: 'Seal Number', autoWidth: 1 },
      ];

      if (userType && userType === UserType.Subcontractor) {
        this.gridHeight = '74vh'
        columns.push({ id: 'subcontractor_instructions', field: 'subcontractorInstructions', text: 'Instructions', autoWidth: 1 });
      } else {
        columns.splice(3, 0, { id: 'freight_payer_name', field: 'freightPayerName', text: 'Freight Payer', autoWidth: 1, ...this.getFilterableCombo('freightPayerName') },)
      }

      const excelFormats = new Map(columns.map(column => [column.field, column.excelFormat]));

      this.gridProps = {
        columns: [
          {
            id: 'action',
            width: '10em',
            text: 'Action',
            field: 'action',
            hidden: false,
            align: 'center',
            type: 'widget',
            hideable: false,
            draggable: false,
            htmlEncode: false,
            filterable: false,
            headerRenderer: ({ record }: any) => {
              return `<label>Action</label><br><button class="btn-xs btn btn-primary clearfilter">Clear Filters</button> `;
            },
            widgets: [
              {
                type: 'button',
                text: 'Options',
                menuIcon: undefined,

                onAction: ({ source: btn }: any) => {
                  let menuItems: Partial<MenuItemConfig>[] = [
                    { text: 'View Job' }
                  ];
                  if (menuItems.length) {
                    const menu = (btn.menu || (btn.menu = new Menu({
                      forElement: btn.element,
                      items: menuItems,
                      onItem: ({ item }) => {
                        if (item.text === 'View Job') {
                          this.openContainerJobEyeView(btn.cellInfo.record.containerJobItemId);
                        }
                      }
                    }))) as Menu;
                    menu.show();
                  }
                }
              }
            ],
          },
          ...columns
        ],
        stateful: true,
        rowCopyPasteFeature: false,
        cellMenuFeature: false,
        filterBarFeature: true,
        cellEditFeature: false,
        cellTooltipFeature: {
          tooltipRenderer: ({ record, column }: any) => record[column.field]
        },
        excelExporterFeature: {
          zipcelx: ({ filename, rows, columns: exportedColumns }: { filename: string, rows: { value: string | number | Date | null | undefined }[][], columns: { field: string, value: string }[] }) => {
            const data = rows.map(row => row.map(({ value }, index): Cell => {
              if (value === null || value === undefined || value === '') {
                return null;
              }
              const format = excelFormats.get(exportedColumns[index].field);
              if (value instanceof Date) {
                return Number.isNaN(value.getTime()) ? null : { value, type: Date, format: format as string };
              }
              if (typeof value === 'number') {
                return { value, type: Number, format: typeof format === 'function' ? format(value) : format };
              }
              return { value, type: String };
            }));
            return writeXlsxFile([
              exportedColumns.map(column => ({ value: column.value, type: String, fontWeight: 'bold' } as Cell)),
              ...data
            ]).toFile(`${filename}.xlsx`);
          }
        },
        store: {
          listeners: {
            change: () => this.updateTotalItemsFromStore(),
            filter: () => this.updateTotalItemsFromStore(),
          }
        },
        listeners: {
          headerClick: (event: any) => this.headerClick(event),
        },
        selectionMode: {
          checkbox: true,
          showCheckAll: true,
          row: false,
          cell: false
        },
      };

      this.isInitializing = false;
    });
  }

  openContainerJobEyeView(containerJobItemId: number) {
    const modalRef = this.modalService.open(ContainerJobEyeViewModalComponent, { size: 'xl' });
    modalRef.componentInstance.containerJobItemId = containerJobItemId;
  }

  headerClick(event: any) {
    if (event && event.domEvent && event.domEvent.target && event.domEvent.target.classList.contains('clearfilter')) {
      if (this.containerGrid && this.containerGrid.instance) {
        (this.containerGrid.instance.store as any).clearFilters();
      }
    }
  }

  toggleAllGroupings(action: 'expand' | 'collapse') {
    if (this.containerGrid && this.containerGrid.instance) {
      if (action === 'collapse') {
        this.containerGrid.instance.collapseAll();
      }

      if (action === 'expand') {
        this.containerGrid.instance.expandAll();
      }
    }
  }

  exportToCsv() {
    if (!this.containerGrid || !this.containerGrid.instance) {
      return;
    }

    const grid = this.containerGrid.instance;
    const columns = (grid.columns as ColumnStore).visibleColumns.filter(column =>
      column.get('exportable') !== false && column.field && column.field !== 'action');
    const date = dayjs().format('YYYY-MM-DD-HH-mm');
    const exportConfig = {
      filename: `trifecta-container-${date}`,
      dateFormat: '',
      exporterConfig: { columns }
    };
    return grid.features.excelExporter.export(exportConfig);
  }

  loadJobsList(dateRange: IContainerSearchState['searchParams']) {
    if (this.isLoadingJobs) {
      this.pendingSearchDateRange = dateRange;
      this.syncGridMask();
      return;
    }

    this.pendingSearchDateRange = null;
    this.isLoadingJobs = true;
    this.syncGridMask();

    this.containerService.getContainerJobLegs(dateRange.from, dateRange.to, dateRange.searchDateOption).pipe(take(1)).subscribe(
      {
        next: (jobs) => {
          if (this.loadPendingJobs()) {
            return;
          }

          this.gridSelected = [];
          this.selectedTotalItems = 0;
          this.data = jobs.map(x => {
            const fullLegCompletedDate = x.inboundFullLegCompletedDateUtc ?? x.outboundFullLegCompletedDateUtc;

            return {
              ...x,
              jobDateLocal: x.jobDateLocal ? new Date(x.jobDateLocal) : null,
              requestedDeliveryDate: x.requestedDeliveryDate ? new Date(x.requestedDeliveryDate) : null,
              fullLegCompletedDate: fullLegCompletedDate ? DateTimeHelper.utcToLocal(fullLegCompletedDate) : null,
              dehireLegCompletedDate: x.dehireLegCompletedDate ? DateTimeHelper.utcToLocal(x.dehireLegCompletedDate) : null,
              devanDate: x.devanDate ? new Date(x.devanDate) : null,
              demurrageDate: DateTimeHelper.parseBryntumDate(x.demurrageDate),
              demurrageTime: DateTimeHelper.parseBryntumTime(x.demurrageTime),
              detentionDate: DateTimeHelper.parseBryntumDate(x.detentionDate),
              detentionTime: DateTimeHelper.parseBryntumTime(x.detentionTime),
              receivalCommenceDate: DateTimeHelper.parseBryntumDate(x.receivalCommenceDate),
              receivalCommenceTime: DateTimeHelper.parseBryntumTime(x.receivalCommenceTime),
              receivalCutoffDate: DateTimeHelper.parseBryntumDate(x.receivalCutoffDate),
              receivalCutoffTime: DateTimeHelper.parseBryntumTime(x.receivalCutoffTime),
              firstNotifiedDate: x.firstNotifiedDate ? new Date(x.firstNotifiedDate) : null,
              docsReceivedDate: x.docsReceivedDate ? new Date(x.docsReceivedDate) : null,
              executionDate: DateTimeHelper.parseBryntumDate(x.executionDate),
              readyForPickupDate: x.readyForPickupDateUtc ? DateTimeHelper.utcToLocal(x.readyForPickupDateUtc) : null,
              containerAvailableDateTime: x.containerAvailableDateTime ? new Date(x.containerAvailableDateTime) : null,
              vesselEtaAndEtdDate: DateTimeHelper.parseBryntumDate(x.vesselEtaAndEtdDate),
              dischargedDate: DateTimeHelper.parseBryntumDate(x.dischargedDate),
              dischargedTime: DateTimeHelper.parseBryntumTime(x.dischargedTime),
              containerLocationName: x.containerLocationType && this.containerLocationTypeThatDisplayEmptyPark.includes(x.containerLocationType) ? "Empty Park" : x.containerLocationName
            }
          }) as ContainerJobGridStatusMovement[];
          this.totalItems = this.data.length;
          queueMicrotask(() => this.updateTotalItemsFromStore());
          this.finishLoadingJobs();
        },
        error: (err) =>{
          if (this.loadPendingJobs()) {
            return;
          }

          this.finishLoadingJobs();
          if (err.status === 401) {
            this.toasterService.showDanger("Refresh token failed. Please log out and log back in.");
          } else {
            this.toasterService.showDanger("Failed to load jobs!", true);
          }
        }
      }
    );
  }

  private loadPendingJobs(): boolean {
    const pendingSearchDateRange = this.pendingSearchDateRange;
    if (!pendingSearchDateRange) {
      return false;
    }

    this.pendingSearchDateRange = null;
    this.isLoadingJobs = false;
    this.loadJobsList(pendingSearchDateRange);
    return true;
  }

  private finishLoadingJobs(): void {
    this.isLoadingJobs = false;
    this.syncGridMask();
  }

  onGridSelectionChange(event: any) {
    this.gridSelected = (event.selection ?? []) as ContainerJobGridStatusMovement[];
    this.selectedTotalItems = this.gridSelected.length;
  }

  async completeJob() {

    if (this.gridSelected.length === 0) {
      return;
    }

    const completedJobSelected = this.gridSelected.find(x => x.status?.statusId === MovementStatusEnum.Completed);

    if (completedJobSelected) {
      this.toasterService.showDanger("Cannot complete a completed job! Please unselect the job!");
      return;
    }

    const modalRef = this.modalService.open(ContainerJobCompleteModalComponent, { size: 'lg' });
    modalRef.componentInstance!.movementIds = this.gridSelected.map(x => x.movementId);

    try {
      const result = await modalRef.result;
      if (result) {
        this.containerGrid?.instance.deselectAll();
        this.loadJobsList(this.searchDateRange!);
      }
    } catch (dismissReason) {
      return;
    }
  }

  printContainerPodPdf(jobId: number) {
    // this.proofOfDeliveryPdfService.printContainerPodPdf(jobId).subscribe({
    //   next: (response) => {
    //     if (response) {
    //       this.blobStorageService.downloadFile(response.fileBytes, response.fileName);
    //     }
    //   },
    //   error: (response: HttpErrorResponse) => {
    //     console.error(response.message, 'An error occurred:');
    //   }
    // });
  }

  getFilterableCombo(field: string) {
    return {
      filterable: {
        filterField: {
          type: 'combo',
          multiSelect: true,
          valueField: field,
          displayField: field
        }
      }
    }
  }

  private syncGridMask() {
    if (!this.containerGrid?.instance || this.containerGrid.instance.isDestroyed) {
      return;
    }

    if (this.isLoadingJobs) {
      this.containerGrid.instance.mask('Loading...');
      return;
    }

    this.containerGrid.instance.unmask();
  }

  private updateTotalItemsFromStore() {
    const gridStore = this.containerGrid?.instance?.store as any;
    if (!gridStore) {
      this.totalItems = this.data.length;
      return;
    }

    const allRecords: any[] = gridStore.allRecords as any[];
    if (!allRecords || allRecords.length === 0) {
      this.totalItems = 0;
      return;
    }

    const filterFn = gridStore.filtersFunction;
    const hasActiveFilterFn = typeof filterFn === 'function';

    let count = 0;
    for (let i = 0; i < allRecords.length; i++) {
      const record = allRecords[i];
      if (record && !record.isSpecialRow && (!hasActiveFilterFn || filterFn(record))) {
        count++;
      }
    }

    this.totalItems = count;
  }
}
